import { useEffect, useState, useMemo } from "react";
import {
  Paper,
  Text,
  Stack,
  Group,
  Badge,
  Tooltip,
  Loader,
  SegmentedControl,
  Box,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBrain,
  IconTool,
  IconClock,
  IconCoin,
  IconListTree,
  IconTimeline,
  IconSettings,
  IconArrowRight,
  IconCode,
} from "@tabler/icons-react";
import { Anchor } from "@mantine/core";

/**
 * Transforms the hierarchical agent execution tree into a flat list of timeline events.
 * Each event has a normalized start time (relative to root) and duration.
 */
function flattenTreeToEvents(node, rootStartTime, events = []) {
  const startOffset = new Date(node.startTime).getTime() - rootStartTime;

  // Add agent event
  events.push({
    id: node.requestId,
    type: "agent",
    label: node.agentRole || "Root Agent",
    depth: node.depth,
    startOffset,
    duration: node.durationMs,
    status: node.status,
    credits: node.creditsUsed,
    totalCredits: node.totalTreeCreditsUsed,
    tokensIn: node.tokensIn,
    tokensOut: node.tokensOut,
  });

  // Add tool call events
  if (node.toolCalls) {
    for (const tool of node.toolCalls) {
      const toolStartOffset = new Date(tool.timestamp).getTime() - rootStartTime;
      events.push({
        id: tool.callId,
        type: "tool",
        label: tool.toolName,
        depth: node.depth + 0.5, // Slightly indented from parent agent
        startOffset: toolStartOffset,
        duration: tool.durationMs,
        status: "completed",
        toolType: tool.toolType,
        parentAgentId: node.requestId,
        parentAgentRole: node.agentRole,
        spawnedRequestId: tool.spawnedRequestId,
        arguments: tool.arguments,
        result: tool.result,
        toolId: tool.toolId, // If available from backend
      });
    }
  }

  // Recurse into children
  if (node.children) {
    for (const child of node.children) {
      flattenTreeToEvents(child, rootStartTime, events);
    }
  }

  return events;
}

/**
 * Timeline bar component representing a single execution event
 */
function TimelineBar({ event, totalDuration, onHover }) {
  const theme = useMantineTheme();
  const isAgent = event.type === "agent";

  // Calculate position and width as percentages
  const leftPercent = (event.startOffset / totalDuration) * 100;
  const widthPercent = Math.max((event.duration / totalDuration) * 100, 0.5); // Min 0.5% for visibility

  // Calculate vertical position based on depth
  const rowHeight = 36;
  const topOffset = event.depth * rowHeight;

  // Color based on type and status
  let bgColor;
  if (!isAgent) {
    bgColor = event.spawnedRequestId ? theme.colors.orange[4] : theme.colors.cyan[4];
  } else if (event.status === "failed") {
    bgColor = theme.colors.red[5];
  } else if (event.status === "timeout") {
    bgColor = theme.colors.yellow[5];
  } else if (event.depth === 0) {
    bgColor = theme.colors.blue[5];
  } else {
    bgColor = theme.colors.indigo[4];
  }

  // Format arguments for display
  const formatArguments = (args) => {
    if (!args) return null;
    try {
      const str = typeof args === "string" ? args : JSON.stringify(args, null, 2);
      // Truncate if too long
      return str.length > 200 ? str.slice(0, 200) + "..." : str;
    } catch {
      return String(args);
    }
  };

  const tooltipContent = (
    <Stack gap={4} style={{ maxWidth: 300 }}>
      <Text size="sm" fw={600}>{event.label}</Text>
      <Group gap="xs">
        <IconClock size={12} />
        <Text size="xs">{event.duration}ms</Text>
      </Group>
      {isAgent && (
        <>
          <Group gap="xs">
            <IconCoin size={12} />
            <Text size="xs">{event.credits?.toLocaleString()} credits (tree: {event.totalCredits?.toLocaleString()})</Text>
          </Group>
          {(event.tokensIn > 0 || event.tokensOut > 0) && (
            <Text size="xs">Tokens: {event.tokensIn} in / {event.tokensOut} out</Text>
          )}
        </>
      )}
      {!isAgent && (
        <>
          {event.spawnedRequestId && (
            <Group gap="xs">
              <IconArrowRight size={12} />
              <Badge size="xs" color="orange">Spawns Sub-Agent</Badge>
            </Group>
          )}
          {event.parentAgentRole && (
            <Text size="xs" c="dimmed">
              Called by: {event.parentAgentRole}
            </Text>
          )}
          {event.arguments && (
            <Stack gap={2}>
              <Group gap={4}>
                <IconCode size={12} />
                <Text size="xs" fw={500}>Arguments:</Text>
              </Group>
              <Text
                size="xs"
                c="dimmed"
                style={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  maxHeight: 80,
                  overflow: "hidden",
                }}
              >
                {formatArguments(event.arguments)}
              </Text>
            </Stack>
          )}
          {event.toolType === 2 && event.toolId && (
            <Anchor
              href={`/tools/${event.toolId}`}
              size="xs"
              onClick={(e) => e.stopPropagation()}
            >
              <Group gap={4}>
                <IconSettings size={12} />
                View tool config
              </Group>
            </Anchor>
          )}
        </>
      )}
      <Text size="xs" c="dimmed">Depth: {Math.floor(event.depth)}</Text>
    </Stack>
  );

  return (
    <Tooltip label={tooltipContent} position="top" withArrow multiline w={220}>
      <Box
        onMouseEnter={() => onHover?.(event)}
        onMouseLeave={() => onHover?.(null)}
        style={{
          position: "absolute",
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
          top: topOffset,
          height: rowHeight - 4,
          backgroundColor: bgColor,
          borderRadius: 4,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          paddingLeft: 6,
          paddingRight: 6,
          overflow: "hidden",
          transition: "opacity 0.15s, transform 0.15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
        className="timeline-bar"
      >
        <Group gap={4} wrap="nowrap" style={{ overflow: "hidden" }}>
          <ThemeIcon size="xs" variant="transparent" color="white">
            {isAgent ? <IconBrain size={12} /> : <IconTool size={12} />}
          </ThemeIcon>
          <Text
            size="xs"
            c="white"
            fw={500}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {event.label}
          </Text>
        </Group>
      </Box>
    </Tooltip>
  );
}

/**
 * Time scale markers for the timeline
 */
function TimeScale({ totalDuration }) {
  // Generate tick marks at reasonable intervals
  const tickCount = 5;
  const ticks = [];

  for (let i = 0; i <= tickCount; i++) {
    const time = (i / tickCount) * totalDuration;
    const label = time < 1000 ? `${Math.round(time)}ms` : `${(time / 1000).toFixed(1)}s`;
    ticks.push({
      position: (i / tickCount) * 100,
      label,
    });
  }

  return (
    <Box style={{ position: "relative", height: 24, borderTop: "1px solid #dee2e6" }}>
      {ticks.map((tick, idx) => (
        <Box
          key={idx}
          style={{
            position: "absolute",
            left: `${tick.position}%`,
            transform: "translateX(-50%)",
          }}
        >
          <Box style={{ width: 1, height: 6, backgroundColor: "#adb5bd", margin: "0 auto" }} />
          <Text size="xs" c="dimmed" ta="center">
            {tick.label}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Depth labels on the left side
 */
function DepthLabels({ maxDepth }) {
  const rowHeight = 36;
  const labels = [];

  for (let i = 0; i <= maxDepth; i++) {
    labels.push(
      <Box
        key={i}
        style={{
          position: "absolute",
          top: i * rowHeight,
          height: rowHeight,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Badge size="xs" variant="light" color="gray">
          L{i}
        </Badge>
      </Box>
    );
  }

  return (
    <Box style={{ position: "relative", width: 40 }}>
      {labels}
    </Box>
  );
}

/**
 * Main execution timeline component
 */
export function ExecutionTimeline({ flowId, runId, tree: propTree }) {
  const [tree, setTree] = useState(propTree || null);
  const [loading, setLoading] = useState(!propTree);
  const [, setHoveredEvent] = useState(null);
  const [viewMode, setViewMode] = useState("timeline");

  useEffect(() => {
    if (propTree) {
      setTree(propTree);
      setLoading(false);
      return;
    }

    if (!flowId || !runId) return;

    fetch(`/api/v1/flows/${flowId}/runs/${runId}/agent-tree`)
      .then((r) => r.json())
      .then((data) => {
        setTree(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch agent tree", err);
        setLoading(false);
      });
  }, [flowId, runId, propTree]);

  // Transform tree to flat events
  const { events, totalDuration, maxDepth } = useMemo(() => {
    if (!tree) return { events: [], totalDuration: 0, maxDepth: 0 };

    const rootStartTime = new Date(tree.startTime).getTime();
    const flatEvents = flattenTreeToEvents(tree, rootStartTime);

    // Calculate total duration and max depth
    let maxEndTime = 0;
    let maxD = 0;
    for (const evt of flatEvents) {
      const endTime = evt.startOffset + evt.duration;
      if (endTime > maxEndTime) maxEndTime = endTime;
      if (evt.depth > maxD) maxD = evt.depth;
    }

    // Sort by start time for consistent rendering
    flatEvents.sort((a, b) => a.startOffset - b.startOffset);

    return {
      events: flatEvents,
      totalDuration: maxEndTime || tree.durationMs || 1000,
      maxDepth: Math.ceil(maxD),
    };
  }, [tree]);

  if (loading) return <Loader size="sm" />;
  if (!tree) return <Text size="sm" c="dimmed">No execution data available</Text>;

  const rowHeight = 36;
  const timelineHeight = (maxDepth + 1) * rowHeight + 8;

  return (
    <Stack gap="xs" mt="md">
      <Group justify="space-between">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed">
          Execution Timeline
        </Text>
        <SegmentedControl
          size="xs"
          value={viewMode}
          onChange={setViewMode}
          data={[
            {
              value: "timeline",
              label: (
                <Group gap={4}>
                  <IconTimeline size={14} />
                  <span>Timeline</span>
                </Group>
              ),
            },
            {
              value: "tree",
              label: (
                <Group gap={4}>
                  <IconListTree size={14} />
                  <span>Tree</span>
                </Group>
              ),
            },
          ]}
        />
      </Group>

      {viewMode === "timeline" ? (
        <Paper withBorder p="md" radius="sm">
          {/* Summary stats */}
          <Group gap="lg" mb="md">
            <Group gap={4}>
              <IconClock size={14} color="gray" />
              <Text size="sm" c="dimmed">
                Total: <Text span fw={600}>{totalDuration < 1000 ? `${totalDuration}ms` : `${(totalDuration / 1000).toFixed(2)}s`}</Text>
              </Text>
            </Group>
            <Group gap={4}>
              <IconBrain size={14} color="gray" />
              <Text size="sm" c="dimmed">
                Agents: <Text span fw={600}>{events.filter(e => e.type === "agent").length}</Text>
              </Text>
            </Group>
            <Group gap={4}>
              <IconTool size={14} color="gray" />
              <Text size="sm" c="dimmed">
                Tools: <Text span fw={600}>{events.filter(e => e.type === "tool").length}</Text>
              </Text>
            </Group>
            <Group gap={4}>
              <IconCoin size={14} color="orange" />
              <Text size="sm" c="dimmed">
                Credits: <Text span fw={600}>{tree.totalTreeCreditsUsed?.toLocaleString()}</Text>
              </Text>
            </Group>
          </Group>

          {/* Timeline visualization */}
          <Group align="flex-start" gap="xs" wrap="nowrap">
            <DepthLabels maxDepth={maxDepth} />
            <Box style={{ flex: 1 }}>
              <Box
                style={{
                  position: "relative",
                  height: timelineHeight,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 4,
                  padding: 4,
                }}
              >
                {events.map((event) => (
                  <TimelineBar
                    key={event.id}
                    event={event}
                    totalDuration={totalDuration}
                    maxDepth={maxDepth}
                    onHover={setHoveredEvent}
                  />
                ))}
              </Box>
              <TimeScale totalDuration={totalDuration} />
            </Box>
          </Group>

          {/* Legend */}
          <Group gap="md" mt="md">
            <Group gap={4}>
              <Box style={{ width: 12, height: 12, backgroundColor: "#339af0", borderRadius: 2 }} />
              <Text size="xs" c="dimmed">Root Agent</Text>
            </Group>
            <Group gap={4}>
              <Box style={{ width: 12, height: 12, backgroundColor: "#5c7cfa", borderRadius: 2 }} />
              <Text size="xs" c="dimmed">Child Agent</Text>
            </Group>
            <Group gap={4}>
              <Box style={{ width: 12, height: 12, backgroundColor: "#22b8cf", borderRadius: 2 }} />
              <Text size="xs" c="dimmed">Tool Call</Text>
            </Group>
            <Group gap={4}>
              <Box style={{ width: 12, height: 12, backgroundColor: "#fd7e14", borderRadius: 2 }} />
              <Text size="xs" c="dimmed">Agent Spawn</Text>
            </Group>
            <Group gap={4}>
              <Box style={{ width: 12, height: 12, backgroundColor: "#fa5252", borderRadius: 2 }} />
              <Text size="xs" c="dimmed">Failed</Text>
            </Group>
          </Group>
        </Paper>
      ) : (
        // Tree view fallback - import the original component
        <TreeViewFallback tree={tree} />
      )}
    </Stack>
  );
}

/**
 * Simple tree view for the "tree" mode option
 */
function TreeViewFallback({ tree }) {
  const renderNode = (node, isRoot = false) => {
    return (
      <Paper
        withBorder
        p="xs"
        radius="sm"
        bg={isRoot ? "blue.0" : "gray.0"}
        style={{ marginBottom: 4 }}
      >
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color={isRoot ? "blue" : "gray"}>
              <IconBrain size={14} />
            </ThemeIcon>
            <Text size="sm" fw={isRoot ? 700 : 500}>
              {node.agentRole || "Root Agent"}
            </Text>
            <Badge
              size="xs"
              variant="outline"
              color={node.status === "succeeded" || node.status === "completed" ? "green" : "red"}
            >
              {node.status}
            </Badge>
          </Group>
          <Group gap="lg">
            <Group gap={4}>
              <IconClock size={12} color="gray" />
              <Text size="xs" c="dimmed">{node.durationMs}ms</Text>
            </Group>
            <Group gap={4}>
              <IconCoin size={12} color="orange" />
              <Text size="xs" fw={500}>{node.totalTreeCreditsUsed?.toLocaleString()} credits</Text>
            </Group>
          </Group>
        </Group>

        {node.children && node.children.length > 0 && (
          <div style={{ marginLeft: 20, marginTop: 8, borderLeft: "1px dashed #dee2e6", paddingLeft: 12 }}>
            {node.children.map((child) => (
              <div key={child.requestId}>{renderNode(child)}</div>
            ))}
          </div>
        )}
      </Paper>
    );
  };

  return renderNode(tree, true);
}

export default ExecutionTimeline;
